class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.refreshFromServer();
  }

  refreshFromServer() {
    // On initial page load, fetch the latest cart-drawer section HTML
    // to bypass any browser/CDN HTML cache serving stale markup.
    try {
      fetch(`${window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart'}?section_id=cart-drawer`, { cache: 'no-store' })
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          if (!text) return;
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const freshDrawer = parsed.querySelector('#CartDrawer');
          const currentDrawer = document.getElementById('CartDrawer');
          if (freshDrawer && currentDrawer && freshDrawer.innerHTML.trim().length > 0) {
            if (currentDrawer.innerHTML.trim() !== freshDrawer.innerHTML.trim()) {
              currentDrawer.innerHTML = freshDrawer.innerHTML;
              this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
            }
          }
        })
        .catch(() => {});
    } catch (e) {}
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Subscription toggle — switches cart line between one-time and selling plan.
// Shopify requires `quantity` in the body when changing `selling_plan`,
// otherwise the line is removed. Always pass the current quantity.
function zenCartChangeSellingPlan(lineKey, sellingPlanId, quantity) {
  const body = {
    id: lineKey,
    quantity: quantity && quantity > 0 ? parseInt(quantity, 10) : 1,
    selling_plan: sellingPlanId ? parseInt(sellingPlanId, 10) : null,
    sections: 'cart-drawer,cart-icon-bubble',
    sections_url: window.location.pathname,
  };
  return fetch(`${window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
    .then(function (response) { return response.ok ? response.json() : null; })
    .then(function (data) {
      if (data && data.sections && data.sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const freshDrawer = parsed.querySelector('#CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');
        if (freshDrawer && currentDrawer) {
          currentDrawer.innerHTML = freshDrawer.innerHTML;
          const overlay = currentDrawer.querySelector('#CartDrawer-Overlay');
          const cd = document.querySelector('cart-drawer');
          if (overlay && cd) overlay.addEventListener('click', cd.close.bind(cd));
        }
      } else {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refreshFromServer === 'function') {
          cartDrawer.refreshFromServer();
        }
      }
    });
}

document.addEventListener('change', function (event) {
  const checkbox = event.target.closest && event.target.closest('[data-subscription-toggle]');
  if (checkbox) {
    const lineKey = checkbox.getAttribute('data-line-key');
    const sellingPlan = checkbox.checked ? checkbox.getAttribute('data-selling-plan') : null;
    if (!lineKey) return;

    // Optimistic UI: instantly show/hide the plan-select without waiting for server
    const subscriptionWrap = checkbox.closest('.sb-cart-drawer__item-subscription');
    const planSelectWrap = subscriptionWrap ? subscriptionWrap.querySelector('[data-plan-select-wrapper]') : null;
    if (planSelectWrap) {
      if (checkbox.checked) {
        planSelectWrap.removeAttribute('hidden');
      } else {
        planSelectWrap.setAttribute('hidden', '');
      }
    }

    // Optimistic UI: also flip the free-shipping progress bar state instantly
    const pbWrapper = document.querySelector('.sb-progress-bar-wrapper');
    const pbBar = pbWrapper ? pbWrapper.querySelector('.sb-progress-bar') : null;
    const prevPbMet = pbWrapper ? pbWrapper.classList.contains('is-met') : false;
    if (pbWrapper && pbBar) {
      if (checkbox.checked) {
        pbWrapper.classList.add('is-met');
        pbBar.classList.add('sb-progress-bar--threshold-met');
        pbBar.setAttribute('data-threshold-met', 'true');
        // Trigger the fill animation (orange → green sweep)
        pbBar.classList.remove('sb-progress-bar--just-filled');
        // Force reflow so re-adding the class restarts the animation
        void pbBar.offsetWidth;
        pbBar.classList.add('sb-progress-bar--just-filled');
      } else {
        // Only flip to "away" if no OTHER subscription item exists in cart
        const otherActive = Array.from(document.querySelectorAll('[data-subscription-toggle]'))
          .some(function (cb) { return cb !== checkbox && cb.checked; });
        if (!otherActive) {
          pbWrapper.classList.remove('is-met');
          pbBar.classList.remove('sb-progress-bar--threshold-met');
          pbBar.classList.remove('sb-progress-bar--just-filled');
          pbBar.setAttribute('data-threshold-met', 'false');
        }
      }
    }

    // Read current quantity from the item
    const itemEl = checkbox.closest('.sb-cart-drawer__item');
    const qtyInput = itemEl ? itemEl.querySelector('.quantity__input') : null;
    const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // Fire server update in background — don't block UI
    zenCartChangeSellingPlan(lineKey, sellingPlan, currentQty)
      .catch(function () {
        // Revert on error
        checkbox.checked = !checkbox.checked;
        if (planSelectWrap) {
          if (checkbox.checked) {
            planSelectWrap.removeAttribute('hidden');
          } else {
            planSelectWrap.setAttribute('hidden', '');
          }
        }
        if (pbWrapper && pbBar) {
          if (prevPbMet) {
            pbWrapper.classList.add('is-met');
            pbBar.classList.add('sb-progress-bar--threshold-met');
          } else {
            pbWrapper.classList.remove('is-met');
            pbBar.classList.remove('sb-progress-bar--threshold-met');
          }
        }
      });
    return;
  }

  const planSelect = event.target.closest && event.target.closest('[data-selling-plan-select]');
  if (planSelect) {
    const lineKey = planSelect.getAttribute('data-line-key');
    const sellingPlan = planSelect.value;
    if (!lineKey || !sellingPlan) return;
    const prevValue = planSelect.dataset.previousValue || planSelect.options[0].value;

    // Read quantity from the same line item
    const itemEl = planSelect.closest('.sb-cart-drawer__item');
    const qtyInput = itemEl ? itemEl.querySelector('.quantity__input') : null;
    const currentQty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    planSelect.disabled = true;
    zenCartChangeSellingPlan(lineKey, sellingPlan, currentQty)
      .then(function () { planSelect.dataset.previousValue = sellingPlan; })
      .catch(function () { planSelect.value = prevValue; })
      .finally(function () { planSelect.disabled = false; });
  }
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
